import React, { useState, useEffect } from 'react'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import {
    Box,
    CircularProgress,
    DialogContent,
    FormHelperText,
} from '@mui/material'
import { H6 } from '../../../../layouts/TextStyles'
import CustomInput from '../../../../components/muiComponents/customInput'
import { courseSubjectApiSlice } from '../../../../features/course/courseSubjectSlice'
import { CourseSubjectState } from '../../../../features/types'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { tone } from '../../../../utils/uiTokens'

const styles = {
    error: {
        color: tone.bad.text,
        marginLeft: 2,
    },
}

interface Props {
    open: boolean
    setOpen: (open: boolean) => void
    courseSubject: CourseSubjectState
}

const EditCourseSubjectDialog = ({ open, setOpen, courseSubject }: Props) => {
    const { formatMessage } = useIntl()
    const [imagePreview, setImagePreview] = useState<string | null>(null)
    const [updateCourseSubject, { isLoading: isUpdating }] =
        courseSubjectApiSlice.useUpdateCourseSubjectMutation()

    const validationSchema = yup.object().shape({
        image: yup.mixed().optional(),
        description: yup.string().optional(),
    })

    const formik = useFormik({
        initialValues: {
            image: courseSubject?.image || '',
            description: courseSubject?.description || '',
        },
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: async () => {
            await handleUpdateCourseSubject()
        },
    })

    useEffect(() => {
        if (open && courseSubject) {
            formik.setValues({
                image: courseSubject.image || '',
                description: courseSubject.description || '',
            })
            setImagePreview(null)
        }
    }, [open, courseSubject])

    const currentImageSrc =
        imagePreview || courseSubject?.image || ''

    const imageLabel = (() => {
        const imageVal: any = (formik as any)?.values?.image
        if (!imageVal || typeof imageVal === 'string') return ''
        return imageVal?.title || imageVal?.name || ''
    })()

    const handleUpdateCourseSubject = async () => {
        try {
            const formData = new FormData()
            formData.append('description', formik.values.description || '')
            const imageVal: any = (formik as any).values.image
            // Добавляем file только если это реальный File объект, а не строка (URL)
            if (imageVal && typeof imageVal !== 'string') {
                formData.append('file', imageVal)
            }

            await updateCourseSubject({
                courseSubjectId: courseSubject.id,
                courseSubject: formData,
            }).unwrap()

            toast.success(formatMessage({ id: 'updatedSuccess' }))
            handleClose()
        } catch (error) {
            toast.error(formatMessage({ id: 'error' }))
        }
    }

    const handleClose = () => {
        setOpen(false)
        setImagePreview(null)
        formik.resetForm()
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]?.size > 10 * 1024 * 1024) {
            toast.error(formatMessage({ id: 'filesDescription' }))
            return
        }
        const file = e.target.files?.[0]
        if (file) {
            formik.setFieldValue('image', file)
            setImagePreview(URL.createObjectURL(file))
        }
    }

    return (
        <Dialog
            maxWidth={'md'}
            open={open}
            onClose={handleClose}
            fullWidth
        >
            <DialogTitle id="responsive-dialog-title">
                {formatMessage({ id: 'editData' })}
            </DialogTitle>

            <DialogContent>
                <form onSubmit={formik.handleSubmit}>
                    <Box mb={2} gap={2}>
                        <H6>{formatMessage({ id: 'portraitPhoto' })}</H6>

                        <Button
                            component="label"
                            variant="outlined"
                            color="primary"
                            startIcon={<FileUploadIcon />}
                        >
                            {formatMessage({ id: 'uploadImage' })}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleImageChange}
                            />
                        </Button>

                        <FormHelperText style={styles.error}>
                            {formik.touched.image && (formik.errors as any)?.image}
                        </FormHelperText>

                        {currentImageSrc && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <img
                                    src={currentImageSrc}
                                    alt="preview"
                                    style={{
                                        width: 80,
                                        height: 80,
                                        objectFit: 'cover',
                                        borderRadius: 8,
                                    }}
                                />
                                {imageLabel && <span>{imageLabel}</span>}
                            </Box>
                        )}
                    </Box>

                    <Box mb={2}>
                        <H6>{formatMessage({ id: 'description' })}</H6>

                        <CustomInput
                            id="description"
                            name="description"
                            placeholder={formatMessage({ id: 'description' })}
                            fullWidth
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.errors.description)}
                            helperText={formik.errors.description}
                            margin="dense"
                            multiline
                            rows={4}
                        />
                    </Box>
                </form>
            </DialogContent>

            <DialogActions sx={{ m: 2, mt: 0 }}>
                <Button
                    onClick={handleClose}
                    variant={'outlined'}
                    sx={{ mr: 1 }}
                    disabled={isUpdating}
                >
                    {formatMessage({ id: 'cancel' })}
                </Button>

                <Button
                    variant={'contained'}
                    sx={{ width: '120px' }}
                    disabled={isUpdating}
                    onClick={() => formik.handleSubmit()}
                >
                    {isUpdating ? (
                        <CircularProgress
                            size={20}
                            style={{ marginRight: '8px' }}
                        />
                    ) : (
                        formatMessage({ id: 'save' })
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default EditCourseSubjectDialog
